import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  App,
  Badge,
  Button,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  BellOutlined,
  CheckCircleFilled,
  DeleteOutlined,
  DownOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  RightOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { authService } from '../services/auth';
import { CustomerOptionDto, SupplierOptionDto, UserOptionDto } from '../dto/reference';
import {
  fetchAccountManagerOptions,
  fetchCustomerOptions,
  fetchNotificationRecipientOptions,
  fetchSupplierOptions,
} from '../modules/reference-data/api/referenceDataApi';
import { matchesCustomerPickerSearch } from '../modules/reference-data/customerSearch';
import { ORDER_CURRENCIES, OrderCurrency } from '../modules/orders/currencies';
import { getDefaultCustomerPaymentDueDate } from '../modules/orders/customerPayment';
import { fetchCustomerProductPrices } from '../modules/customers/api/customerProductPricesApi';
import { formatCurrency } from '../utils/currency';
import { formatProductDimensions } from '../utils/productDimensions';
import { formatUnit } from '../utils/units';
import OrderDraftToolbar, { OrderDraftToolbarHandle } from '../components/orders/OrderDraftToolbar';
import PresenceBanner from '../components/presence/PresenceBanner';
import { usePresence } from '../hooks/usePresence';
import VariantPicker from '../components/inventory/VariantPicker';
import { getVariantOrderLinePatch } from '../modules/products/orderVariantSelection';
import {
  createOrderFromWizard,
  fetchOrderSettings,
  fetchSuggestedManualOrderNumber,
} from '../modules/orders/api/ordersApi';
import {
  getLineCurrency,
  getLineTotal,
  getTotalsByCurrency,
  OrderProductLineDraft,
} from '../modules/orders/orderProductLineTotals';
import {
  getOrderSaleUnit,
  SALE_UNITS,
} from '../modules/orders/orderProductSaleQuantity';
import {
  omitUnusedOrderProductHeight,
} from '../modules/orders/orderProductDimensions';
import { getOrderProductSaveErrorMessage } from '../modules/orders/orderProductSaveErrors';
import { customerOrderNumberRule } from '../dto/order';
import './OrderCreateWizard.css';

const { Text, Title } = Typography;

const MAX_ITEMS = 200;
type FieldPath = string | (string | number)[];

type ProductLine = OrderProductLineDraft;

const getStepFields = (
  step: number,
  products: ProductLine[] = [],
  useManualNumbering = false,
  requireSalePrices = true
): FieldPath[] => {
  if (step === 0) {
    return useManualNumbering
      ? ['customerId', 'manualOrderNumber', 'orderDate']
      : ['customerId', 'orderDate'];
  }

  if (step === 1) {
    const paths: FieldPath[] = [];
    products.forEach((_, idx) => {
      paths.push(['products', idx, 'productVariantId']);
      paths.push(['products', idx, 'quantity']);
      if (requireSalePrices) {
        paths.push(['products', idx, 'unitPrice']);
        paths.push(['products', idx, 'currency']);
      }
    });
    return paths;
  }

  return [];
};

// Drafts persist server-side as JSON, so dayjs date fields round-trip as ISO
// strings. These mirror the date fields the wizard puts on the form.
function serializeOrderDraftValues(values: any): Record<string, any> {
  const serializable = { ...values };
  if (serializable.orderDate && dayjs.isDayjs(serializable.orderDate)) {
    serializable.orderDate = serializable.orderDate.toISOString();
  }
  if (serializable.customerPaymentDueDate && dayjs.isDayjs(serializable.customerPaymentDueDate)) {
    serializable.customerPaymentDueDate = serializable.customerPaymentDueDate.toISOString();
  }
  if (serializable.quoteValidUntil && dayjs.isDayjs(serializable.quoteValidUntil)) {
    serializable.quoteValidUntil = serializable.quoteValidUntil.toISOString();
  }
  if (serializable.expectedDelivery && dayjs.isDayjs(serializable.expectedDelivery)) {
    serializable.expectedDelivery = serializable.expectedDelivery.toISOString();
  }
  return serializable;
}

function deserializeOrderDraftPayload(payload: Record<string, any>): Record<string, any> {
  const parsed = { ...payload };
  if (parsed.orderDate) parsed.orderDate = dayjs(parsed.orderDate);
  if (parsed.customerPaymentDueDate) parsed.customerPaymentDueDate = dayjs(parsed.customerPaymentDueDate);
  if (parsed.quoteValidUntil) parsed.quoteValidUntil = dayjs(parsed.quoteValidUntil);
  if (parsed.expectedDelivery) parsed.expectedDelivery = dayjs(parsed.expectedDelivery);
  return parsed;
}

const OrderCreateWizard: React.FC = () => {
  const { t } = useTranslation(['orders', 'common', 'customers']);
  const navigate = useNavigate();
  const { message: appMessage, modal } = App.useApp();
  const [form] = Form.useForm();

  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [customers, setCustomers] = useState<CustomerOptionDto[]>([]);
  const [suppliers, setSuppliers] = useState<SupplierOptionDto[]>([]);
  const [accountManagerOptions, setAccountManagerOptions] = useState<UserOptionDto[]>([]);
  const [notificationRecipientOptions, setNotificationRecipientOptions] = useState<UserOptionDto[]>([]);
  const [userSettings, setUserSettings] = useState<{ currency: string } | null>(null);
  const [paymentDueDateTouched, setPaymentDueDateTouched] = useState(false);
  const [customerPriceByVariant, setCustomerPriceByVariant] = useState<
    Map<string, { fixedPrice: number; currency: string }>
  >(new Map());
  const [lastDraftSavedAt, setLastDraftSavedAt] = useState<Date | null>(null);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const initializedRef = useRef(false);
  const draftToolbarRef = useRef<OrderDraftToolbarHandle>(null);
  // Last value the suggest endpoint proposed for the manual number. Lets the
  // order-date sync effect refresh the suggestion without clobbering a number
  // the operator typed over it.
  const lastSuggestedNumberRef = useRef<string | null>(null);
  const [autoNumberPreview, setAutoNumberPreview] = useState<string | null>(null);

  const watchedCustomerId = Form.useWatch('customerId', form);
  const watchedProducts = Form.useWatch('products', form) as ProductLine[] | undefined;
  const watchedOrderDate = Form.useWatch('orderDate', form);
  const watchedExpectedDelivery = Form.useWatch('expectedDelivery', form);
  const watchedClientOrderNumber = Form.useWatch('clientOrderNumber', form);
  const watchedFactoryLocation = Form.useWatch('factoryLocation', form);
  const watchedDeliveryLocation = Form.useWatch('deliveryLocation', form);
  const watchedCustomerPaymentDueDate = Form.useWatch('customerPaymentDueDate', form);
  const watchedUseManualNumbering = Form.useWatch('useManualNumbering', form);
  const watchedManualOrderNumber = Form.useWatch('manualOrderNumber', form);
  const presenceOthers = usePresence('CUSTOMER_CREATE', watchedCustomerId || null);

  const canViewCustomerPayments = authService.canViewCustomerPayments();
  const canEditCustomerSalePrices = authService.canEditCustomerSalePrices();
  const fallbackCurrency = userSettings?.currency || 'PLN';
  const products = useMemo<ProductLine[]>(
    () => (watchedProducts || form.getFieldValue('products') || []) as ProductLine[],
    [watchedProducts, form]
  );
  const totalsByCurrency = useMemo(
    () => canEditCustomerSalePrices ? getTotalsByCurrency(products, fallbackCurrency) : {},
    [canEditCustomerSalePrices, products, fallbackCurrency]
  );

  const customerNamesById = useMemo(
    () => new Map(customers.map((c) => [c.id, c.name] as const)),
    [customers]
  );

  const selectedCustomer = useMemo(
    () => customers.find((customer) => customer.id === watchedCustomerId),
    [customers, watchedCustomerId]
  );

  const customerSelectOptions = useMemo(
    () =>
      customers.map((customer) => ({
        value: customer.id,
        label: customer.name,
        name: customer.name,
        taxId: customer.taxId || null,
        isBranch: Boolean(customer.isBranch),
        parentName: customer.parentCustomerId
          ? customerNamesById.get(customer.parentCustomerId) || null
          : null,
      })),
    [customerNamesById, customers]
  );

  const filterCustomerOption = (
    input: string,
    option?: { name?: string; taxId?: string | null; parentName?: string | null }
  ) =>
    matchesCustomerPickerSearch(
      {
        name: option?.name || '',
        taxId: option?.taxId || null,
        parentName: option?.parentName || null,
      },
      input
    );

  const renderCustomerOption = (option: any) => {
    const { label, taxId, isBranch, parentName } = option.data as {
      label: string;
      taxId?: string | null;
      isBranch?: boolean;
      parentName?: string | null;
    };

    return (
      <div className="order-create-customer-option">
        <Space size={8}>
          <span>{label}</span>
          {isBranch ? <Tag bordered={false}>{t('customers:preview.branch')}</Tag> : null}
        </Space>
        {parentName ? (
          <Text type="secondary" className="order-create-option-meta">
            {t('customers:preview.parentCustomer')}: {parentName}
          </Text>
        ) : null}
        {taxId ? (
          <Text type="secondary" className="order-create-option-meta">
            NIP: {taxId}
          </Text>
        ) : null}
      </div>
    );
  };

  const buildDraftPayload = () => serializeOrderDraftValues(form.getFieldsValue(true));

  const applyDraftPayload = (payload: Record<string, any>) => {
    form.setFieldsValue(deserializeOrderDraftPayload(payload));
  };

  const setDefaultPaymentDueDate = (customerId?: string | null, baseDate?: dayjs.Dayjs | null) => {
    const orderDate = baseDate || form.getFieldValue('orderDate') || dayjs();
    const customer = customers.find(
      (item) => item.id === (customerId || form.getFieldValue('customerId'))
    );
    form.setFieldValue('customerPaymentDueDate', getDefaultCustomerPaymentDueDate(orderDate, customer));
  };

  const handleOrderDateChange = (date: dayjs.Dayjs | null) => {
    if (!paymentDueDateTouched) {
      setDefaultPaymentDueDate(form.getFieldValue('customerId'), date || dayjs());
    }
  };

  const handleOrderCustomerChange = (customerId?: string) => {
    if (!paymentDueDateTouched) {
      setDefaultPaymentDueDate(customerId || null);
    }
  };

  const handleManualNumberingChange = (checked: boolean) => {
    form.setFieldValue('useManualNumbering', checked);
    if (!checked) {
      form.setFieldValue('manualOrderNumber', '');
      lastSuggestedNumberRef.current = null;
    }
    // When turned on, the suggested number is fetched by the sync effect below
    // (keyed on the order date) so it always reflects the chosen business date.
  };

  // Surface the predicted auto number, scoped to the chosen order date: a
  // back-dated order should be numbered in its own month, mirroring how the
  // backend scopes numbering by orderDate. Always fetched so automatic mode can
  // show it read-only; in manual mode it also pre-fills the editable field, but
  // only while that field still holds the last suggestion — never clobber an
  // operator's own typed value. The value is a prediction; the binding number is
  // assigned server-side at create time.
  useEffect(() => {
    const orderDateValue = form.getFieldValue('orderDate');
    const orderDateIso = dayjs.isDayjs(orderDateValue) ? orderDateValue.toISOString() : undefined;
    let ignore = false;
    (async () => {
      try {
        const suggestion = await fetchSuggestedManualOrderNumber(orderDateIso);
        if (ignore) return;
        setAutoNumberPreview(suggestion);
        if (watchedUseManualNumbering) {
          const current = form.getFieldValue('manualOrderNumber');
          if (!current || current === lastSuggestedNumberRef.current) {
            form.setFieldValue('manualOrderNumber', suggestion);
            lastSuggestedNumberRef.current = suggestion;
          }
        }
      } catch {
        // Non-critical — user can type manually
      }
    })();
    return () => {
      ignore = true;
    };
  }, [watchedUseManualNumbering, watchedOrderDate, form]);

  const fetchCustomers = async () => {
    try {
      const response = await fetchCustomerOptions('orders.customerOptions');
      setCustomers(response);
    } catch {
      // Non-critical — select will be empty
    }
  };

  const fetchSuppliers = async () => {
    try {
      setSuppliers(await fetchSupplierOptions('orders.create.suppliers'));
    } catch {
      // Non-critical
    }
  };

  const fetchAccountManagers = async () => {
    try {
      setAccountManagerOptions(await fetchAccountManagerOptions('orders.create.accountManagers'));
    } catch {
      // Non-critical
    }
  };

  const fetchNotificationRecipients = async () => {
    try {
      setNotificationRecipientOptions(
        await fetchNotificationRecipientOptions('orders.create.notificationRecipients')
      );
    } catch {
      // Non-critical
    }
  };

  const fetchUserSettings = async () => {
    try {
      const settings = await fetchOrderSettings();
      setUserSettings({ currency: settings.currency || 'PLN' });
    } catch {
      setUserSettings({ currency: 'PLN' });
    }
  };

  const patchProduct = (index: number, patch: Partial<ProductLine>) => {
    const currentProducts = [...(form.getFieldValue('products') || [])];
    currentProducts[index] = {
      ...(currentProducts[index] || {}),
      ...patch,
    };
    form.setFieldValue('products', currentProducts);
  };

  useEffect(() => {
    const init = async () => {
      await Promise.allSettled([
        fetchCustomers(),
        fetchSuppliers(),
        fetchAccountManagers(),
        fetchNotificationRecipients(),
        fetchUserSettings(),
      ]);
    };

    void init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!watchedCustomerId || !canEditCustomerSalePrices) {
      setCustomerPriceByVariant(new Map());
      return;
    }

    let cancelled = false;
    const loadPrices = async () => {
      try {
        const prices = await fetchCustomerProductPrices(watchedCustomerId);
        if (cancelled) return;
        setCustomerPriceByVariant(
          new Map(
            prices.map((price) => [
              price.productVariantId,
              { fixedPrice: price.fixedPrice, currency: price.currency }
            ])
          )
        );
      } catch {
        if (!cancelled) {
          setCustomerPriceByVariant(new Map());
        }
      }
    };

    void loadPrices();
    return () => {
      cancelled = true;
    };
  }, [canEditCustomerSalePrices, watchedCustomerId]);

  useEffect(() => {
    if (initializedRef.current || !userSettings) return;
    initializedRef.current = true;

    const defaultCurrency = userSettings.currency || 'PLN';
    const defaults = {
      currency: defaultCurrency,
      orderDate: dayjs(),
      customerPaymentDueDate: getDefaultCustomerPaymentDueDate(dayjs()),
      useManualNumbering: false,
      isQuote: false,
      products: [{ name: '', quantity: 1, unitPrice: 0, unit: 'pcs', currency: defaultCurrency }],
    };

    form.setFieldsValue(defaults);
  }, [userSettings, form]);

  const handleNext = async () => {
    const currentProducts = form.getFieldValue('products') || [];
    if (currentStep === 1 && currentProducts.length === 0) {
      appMessage.error(t('orders:modal.validation.atLeastOneProductRequired'));
      return;
    }
    const useManualNumbering = Boolean(form.getFieldValue('useManualNumbering'));
    const fieldsToValidate = getStepFields(
      currentStep,
      currentProducts,
      useManualNumbering,
      canEditCustomerSalePrices
    );
    if (fieldsToValidate.length > 0) {
      try {
        await form.validateFields(fieldsToValidate);
      } catch {
        return;
      }
    }
    setCurrentStep((s) => s + 1);
  };

  const handleBack = () => {
    setCurrentStep((s) => s - 1);
  };

  const handleCancel = () => {
    if (form.isFieldsTouched()) {
      modal.confirm({
        title: t('orders:wizard.confirmCancelTitle'),
        content: t('orders:wizard.confirmCancelContent'),
        okText: t('common:yes'),
        cancelText: t('common:no'),
        onOk: () => {
          navigate('/app/orders');
        },
      });
    } else {
      navigate('/app/orders');
    }
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const values = await form.validateFields();
      const normalizedValues = {
        ...values,
        products: (values.products || []).map((product: ProductLine) => {
          if (!canEditCustomerSalePrices) {
            const { currency, unitPrice, ...operationalProduct } = product;
            return omitUnusedOrderProductHeight(operationalProduct);
          }

          return omitUnusedOrderProductHeight({
            ...product,
            unitPrice: product.unitPrice,
            currency: product.currency || userSettings?.currency || 'PLN',
          });
        }),
      };
      const createdOrder = await createOrderFromWizard(normalizedValues, userSettings?.currency || 'PLN', {
        includeProductSalePrices: canEditCustomerSalePrices,
      });
      await draftToolbarRef.current?.discardLoadedDraft();
      appMessage.success(t('orders:messages.createSuccess'));
      if (createdOrder.id) {
        navigate(`/app/orders/${createdOrder.id}`);
      } else {
        navigate('/app/orders');
      }
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      appMessage.error(getOrderProductSaveErrorMessage(error, t('orders:messages.createError')));
    } finally {
      setSubmitting(false);
    }
  };

  const stepItems = [
    {
      key: 'customer',
      title: t('orders:wizard.steps.customer'),
      description: t('orders:wizard.stepDescriptions.customer'),
    },
    {
      key: 'products',
      title: t('orders:wizard.steps.products'),
      description: canEditCustomerSalePrices
        ? t('orders:wizard.stepDescriptions.productsWithPrices')
        : t('orders:wizard.stepDescriptions.products'),
    },
    {
      key: 'terms',
      title: t('orders:wizard.steps.terms'),
      description: t('orders:wizard.stepDescriptions.terms'),
    },
    {
      key: 'summary',
      title: t('orders:wizard.steps.summary'),
      description: t('orders:wizard.stepDescriptions.summary'),
    },
  ];

  const renderSectionHeader = (title: string, subtitle?: string) => (
    <div className="order-create-section-header">
      <Title level={3}>{title}</Title>
      {subtitle ? <Text type="secondary">{subtitle}</Text> : null}
    </div>
  );

  const renderStepRail = () => (
    <nav className="order-create-step-rail" aria-label={t('orders:wizard.progressLabel')}>
      {stepItems.map((item, index) => {
        const isActive = index === currentStep;
        const isComplete = index < currentStep;
        return (
          <button
            key={item.key}
            type="button"
            className={`order-create-step ${isActive ? 'is-active' : ''} ${isComplete ? 'is-complete' : ''}`}
            onClick={() => {
              if (index <= currentStep) {
                setCurrentStep(index);
              }
            }}
            disabled={index > currentStep}
          >
            <span className="order-create-step-index">
              {isComplete ? <CheckCircleFilled /> : index + 1}
            </span>
            <span className="order-create-step-copy">
              <strong>{item.title}</strong>
              <span>{item.description}</span>
            </span>
          </button>
        );
      })}
    </nav>
  );

  const renderTotals = (mode: 'compact' | 'full' = 'full') => {
    const entries = Object.entries(totalsByCurrency);
    if (entries.length === 0) {
      return <Text type="secondary">{t('orders:wizard.summary.noTotals')}</Text>;
    }

    return (
      <div className={`order-create-totals order-create-totals-${mode}`}>
        {entries.map(([currency, total]) => (
          <div key={currency} className="order-create-total-row">
            <span>{currency}</span>
            <strong>{formatCurrency(total, currency)}</strong>
          </div>
        ))}
      </div>
    );
  };

  const renderQuantity = (value: number | string | null | undefined) => {
    const numericValue = Number(value ?? 0);
    return Number.isFinite(numericValue)
      ? numericValue.toLocaleString('pl-PL', { maximumFractionDigits: 3 })
      : '-';
  };

  const renderDraftSummary = () => {
    const filledProducts = products.filter((product) => String(product?.name || '').trim());
    const hasRevenueTotals = Object.keys(totalsByCurrency).length > 0;
    const hasMixedCurrencies = Object.keys(totalsByCurrency).length > 1;

    return (
      <aside
        className={`order-create-summary-panel ${
          summaryExpanded ? 'is-expanded' : 'is-collapsed'
        }`}
      >
        <div className="order-create-summary-heading">
          <div>
            <Text type="secondary">{t('orders:wizard.summaryPanel.eyebrow')}</Text>
            <Title level={4}>{t('orders:wizard.summaryPanel.title')}</Title>
          </div>
          <Space size={8}>
            <Badge status={lastDraftSavedAt ? 'success' : 'default'} />
            <Button
              type="text"
              size="small"
              icon={summaryExpanded ? <UpOutlined /> : <DownOutlined />}
              onClick={() => setSummaryExpanded((expanded) => !expanded)}
            >
              {summaryExpanded
                ? t('orders:wizard.summaryPanel.collapse')
                : t('orders:wizard.summaryPanel.expand')}
            </Button>
          </Space>
        </div>

        <div className="order-create-summary-compact">
          <div>
            <span>{t('orders:wizard.summary.customer')}</span>
            <strong>{selectedCustomer?.name || '-'}</strong>
          </div>
          <div>
            <span>{t('orders:wizard.summary.orderDate')}</span>
            <strong>{watchedOrderDate ? dayjs(watchedOrderDate).format('YYYY-MM-DD') : '-'}</strong>
          </div>
          <div>
            <span>{t('orders:modal.generatedNumberLabel')}</span>
            <strong>
              {watchedUseManualNumbering
                ? watchedManualOrderNumber || '-'
                : autoNumberPreview || '-'}
            </strong>
          </div>
          <div>
            <span>{t('orders:wizard.summary.productsCount')}</span>
            <strong>{filledProducts.length}</strong>
          </div>
          {canEditCustomerSalePrices && hasRevenueTotals ? (
            <div>
              <span>{t('orders:wizard.summary.totalRevenue')}</span>
              {renderTotals('compact')}
            </div>
          ) : null}
        </div>

        {summaryExpanded ? (
          <div className="order-create-summary-details">
            <Divider />

            <div className="order-create-summary-list">
              <div>
                <span>{t('orders:modal.clientOrderNumber')}</span>
                <strong>{watchedClientOrderNumber || '-'}</strong>
              </div>
              <div>
                <span>{t('orders:expectedDelivery')}</span>
                <strong>
                  {watchedExpectedDelivery
                    ? dayjs(watchedExpectedDelivery).format('YYYY-MM-DD')
                    : '-'}
                </strong>
              </div>
              {canViewCustomerPayments ? (
                <div>
                  <span>{t('orders:modal.customerPaymentDueDate')}</span>
                  <strong>
                    {watchedCustomerPaymentDueDate
                      ? dayjs(watchedCustomerPaymentDueDate).format('YYYY-MM-DD')
                      : '-'}
                  </strong>
                </div>
              ) : null}
            </div>

            {hasMixedCurrencies ? (
              <Alert
                type="warning"
                showIcon
                message={t('orders:wizard.summaryPanel.mixedCurrency')}
                className="order-create-summary-alert"
              />
            ) : null}
          </div>
        ) : null}

        <div className="order-create-draft-status">
          <CheckCircleFilled />
          <span>
            {lastDraftSavedAt
              ? t('orders:wizard.summaryPanel.draftSaved')
              : t('orders:wizard.summaryPanel.draftReady')}
          </span>
        </div>
      </aside>
    );
  };

  const renderStep0 = () => (
    <div className="order-create-section">
      {renderSectionHeader(
        t('orders:wizard.sectionTitles.customer'),
        t('orders:wizard.sectionSubtitles.customer')
      )}

      <Row gutter={[20, 0]}>
        <Col xs={24} lg={14}>
          <Form.Item
            name="customerId"
            label={t('orders:customer')}
            rules={[{ required: true, message: t('orders:modal.validation.customerRequired') }]}
          >
            <Select
              placeholder={t('orders:modal.selectCustomer')}
              showSearch
              allowClear
              filterOption={filterCustomerOption}
              options={customerSelectOptions}
              optionRender={renderCustomerOption}
              onChange={handleOrderCustomerChange}
              onDropdownVisibleChange={(open) => {
                if (open) void fetchCustomers();
              }}
            />
          </Form.Item>
        </Col>
        <Col xs={24} lg={10}>
          <Form.Item
            name="orderDate"
            label={t('orders:modal.orderDate')}
            rules={[{ required: true, message: t('orders:modal.validation.orderDateRequired') }]}
          >
            <DatePicker style={{ width: '100%' }} onChange={handleOrderDateChange} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[20, 0]}>
        <Col xs={24} lg={12}>
          <Form.Item
            name="useManualNumbering"
            label={t('orders:modal.numberingMode')}
            valuePropName="checked"
          >
            <Switch
              checkedChildren={t('orders:modal.manualNumberingShort')}
              unCheckedChildren={t('orders:modal.automaticNumberingShort')}
              onChange={handleManualNumberingChange}
            />
          </Form.Item>
          {!watchedUseManualNumbering ? (
            <Text type="secondary" className="order-create-helper">
              {t('orders:modal.automaticNumberingHint')}
            </Text>
          ) : null}
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.useManualNumbering !== curr.useManualNumbering}
          >
            {({ getFieldValue }) =>
              getFieldValue('useManualNumbering') ? (
                <Form.Item
                  name="manualOrderNumber"
                  label={t('orders:modal.manualOrderNumber')}
                  rules={[
                    { required: true, message: t('orders:modal.validation.manualNumberRequired') },
                    customerOrderNumberRule(t),
                  ]}
                >
                  <Input maxLength={64} placeholder={t('orders:modal.manualOrderNumberPlaceholder')} />
                </Form.Item>
              ) : (
                <Form.Item label={t('orders:modal.generatedNumberLabel')}>
                  <div className="order-create-generated-number">
                    <Text strong style={{ fontSize: 16 }}>{autoNumberPreview || '—'}</Text>
                  </div>
                  <Text
                    type="secondary"
                    className="order-create-helper order-create-generated-number-hint"
                  >
                    {t('orders:modal.generatedNumberPreview')}
                  </Text>
                </Form.Item>
              )
            }
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[20, 0]}>
        <Col xs={24} lg={12}>
          <Form.Item name="clientOrderNumber" label={t('orders:modal.clientOrderNumber')}>
            <Input placeholder={t('orders:modal.clientOrderNumberPlaceholder')} />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item
            name="accountManagerId"
            label={t('orders:modal.accountManager')}
            tooltip={t('orders:modal.accountManagerTooltip')}
          >
            <Select
              placeholder={t('orders:modal.selectAccountManager')}
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {accountManagerOptions.map((user) => (
                <Select.Option key={user.id} value={user.id}>
                  {user.name} ({user.email})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
    </div>
  );

  const renderProductColumns = (fields: any[], remove: (index: number | number[]) => void) => [
    {
      title: t('orders:modal.productVariant'),
      dataIndex: 'productVariantId',
      width: 240,
      render: (_: unknown, field: any) => (
        <>
          <Form.Item
            name={[field.name, 'productVariantId']}
            style={{ margin: 0 }}
            rules={[{ required: true, message: t('orders:modal.validation.productRequired') }]}
          >
            <VariantPicker
              allowCreate
              placeholder={t('orders:orderDetail.variantPickerPlaceholder')}
              showInventory={false}
              data-testid={`order-product-row-${field.name}-variant`}
              onChange={(variantId, variant) => {
                const currentProduct = form.getFieldValue(['products', field.name]) || {};
                const fixedPrice = canEditCustomerSalePrices && variantId
                  ? customerPriceByVariant.get(variantId)
                  : undefined;
                patchProduct(field.name, omitUnusedOrderProductHeight({
                  ...getVariantOrderLinePatch(
                    variantId,
                    variant,
                    currentProduct,
                    fixedPrice,
                  ),
                }));
                if (fixedPrice) {
                  appMessage.success(t('orders:orderDetail.customerPriceApplied'));
                }
              }}
            />
          </Form.Item>
          {/* Registered for submission as the immutable document snapshot;
              it is intentionally not a second operator-facing product field. */}
          <Form.Item name={[field.name, 'name']} hidden>
            <Input />
          </Form.Item>
        </>
      ),
    },
    {
      title: t('orders:modal.dimensionsShort'),
      key: 'dimensions',
      width: 150,
      render: (_: unknown, field: any) => formatProductDimensions(products[field.name]) || '-',
    },
    {
      title: t('orders:quantity'),
      dataIndex: 'quantity',
      width: 120,
      render: (_: unknown, field: any) => (
        <Form.Item
          name={[field.name, 'quantity']}
          style={{ margin: 0 }}
          rules={[{ required: true, message: t('orders:modal.validation.quantityRequired') }]}
        >
          <InputNumber
            min={0.001}
            max={1000000}
            step={0.001}
            precision={3}
            className="order-create-number-input"
            data-testid={`order-product-row-${field.name}-quantity`}
          />
        </Form.Item>
      ),
    },
    {
      title: t('orders:modal.unit'),
      dataIndex: 'unit',
      width: 100,
      render: (_: unknown, field: any) => (
        <Form.Item name={[field.name, 'unit']} style={{ margin: 0 }} initialValue="pcs">
          <Select data-testid={`order-product-row-${field.name}-unit`}>
            {SALE_UNITS.map((unit) => (
              <Select.Option key={unit} value={unit}>
                {formatUnit(unit)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      ),
    },
    ...(canEditCustomerSalePrices ? [
      {
        title: t('orders:unitPrice'),
        dataIndex: 'unitPrice',
        width: 130,
        render: (_: unknown, field: any) => (
          <Form.Item
            name={[field.name, 'unitPrice']}
            style={{ margin: 0 }}
            rules={[{ required: true, message: t('orders:modal.validation.unitPriceRequired') }]}
          >
            <InputNumber
              min={0}
              max={1000000}
              precision={2}
              className="order-create-number-input"
              data-testid={`order-product-row-${field.name}-unitPrice`}
            />
          </Form.Item>
        ),
      },
      {
        title: t('orders:orderDetail.currency'),
        dataIndex: 'currency',
        width: 112,
        render: (_: unknown, field: any) => (
          <Form.Item
            name={[field.name, 'currency']}
            style={{ margin: 0 }}
            rules={[{ required: true, message: t('orders:modal.validation.currencyRequired') }]}
          >
            <Select
              aria-label={t('orders:orderDetail.currency')}
              data-testid={`order-product-row-${field.name}-currency`}
            >
              {ORDER_CURRENCIES.map((currency: OrderCurrency) => (
                <Select.Option key={currency} value={currency}>
                  {currency}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        ),
      },
      {
        title: t('orders:totalPrice'),
        key: 'lineTotal',
        width: 150,
        render: (_: unknown, field: any) => {
          const product = products[field.name];
          const currency = getLineCurrency(product, fallbackCurrency);
          return <strong>{formatCurrency(getLineTotal(product), currency)}</strong>;
        },
      },
    ] : []),
    {
      title: '',
      key: 'actions',
      width: 56,
      render: (_: unknown, field: any) => (
        <Tooltip title={t('orders:modal.removeProduct')}>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => remove(field.name)}
            aria-label={t('orders:modal.removeProduct')}
          />
        </Tooltip>
      ),
    },
  ];

  const renderStep1 = () => (
    <div className="order-create-section">
      {renderSectionHeader(
        canEditCustomerSalePrices
          ? t('orders:wizard.sectionTitles.productsWithPrices')
          : t('orders:wizard.sectionTitles.products'),
        canEditCustomerSalePrices
          ? t('orders:wizard.sectionSubtitles.productsWithPrices')
          : t('orders:wizard.sectionSubtitles.products')
      )}

      {canEditCustomerSalePrices ? (
        <Alert
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          message={t('orders:wizard.lineCurrencyNotice')}
          className="order-create-inline-alert"
        />
      ) : null}

      <Form.List
        name="products"
        initialValue={[{ name: '', quantity: 1, unitPrice: 0, unit: 'pcs', currency: fallbackCurrency }]}
      >
        {(fields, { add, remove }) => (
          <>
            {/* tableLayout="fixed" is load-bearing: scroll.x alone keeps antd on
                table-layout:auto, where a long nowrap VariantPicker label expands
                the variant column and crushes the width:100% dimension/qty/price
                inputs. Every column must keep an explicit width summing to scroll.x. */}
            <Table
              className="order-create-products-table"
              dataSource={fields}
              columns={renderProductColumns(fields, remove)}
              rowKey="key"
              pagination={false}
              scroll={{ x: canEditCustomerSalePrices ? 1060 : 670 }}
              tableLayout="fixed"
            />

            <div className="order-create-products-footer">
              <Button
                type="dashed"
                onClick={() => add({ name: '', quantity: 1, unitPrice: 0, unit: 'pcs', currency: fallbackCurrency })}
                icon={<PlusOutlined />}
                disabled={fields.length >= MAX_ITEMS}
              >
                {t('orders:modal.addProduct')}
              </Button>
            </div>
          </>
        )}
      </Form.List>
    </div>
  );

  const renderStep2 = () => (
    <div className="order-create-section">
      {renderSectionHeader(
        t('orders:wizard.sectionTitles.terms'),
        t('orders:wizard.sectionSubtitles.terms')
      )}

      <Row gutter={[20, 0]}>
        <Col xs={24} lg={12}>
          <Form.Item name="supplierId" label={t('orders:modal.supplier')}>
            <Select
              placeholder={t('orders:modal.selectSupplier')}
              allowClear
              showSearch
              optionFilterProp="searchText"
              options={suppliers.map((supplier) => ({
                value: supplier.id,
                label: supplier.name,
                searchText: supplier.searchText
              }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item name="expectedDelivery" label={t('orders:expectedDelivery')}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[20, 0]}>
        <Col xs={24} lg={12}>
          <Form.Item name="factoryLocation" label={t('orders:modal.factoryLocation')}>
            <Input.TextArea rows={3} placeholder={t('orders:modal.factoryLocationPlaceholder')} />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item name="deliveryLocation" label={t('orders:modal.deliveryLocation')}>
            <Input.TextArea rows={3} placeholder={t('orders:modal.deliveryLocationPlaceholder')} />
          </Form.Item>
        </Col>
      </Row>

      <div className="order-create-route-preview">
        <div>
          <span>{t('orders:wizard.route.loading')}</span>
          <strong>{watchedFactoryLocation || '-'}</strong>
        </div>
        <RightOutlined />
        <div>
          <span>{t('orders:wizard.route.delivery')}</span>
          <strong>{watchedDeliveryLocation || '-'}</strong>
        </div>
      </div>

      {canViewCustomerPayments && (
        <>
          <Divider />
          <Row gutter={[20, 0]}>
            <Col xs={24} lg={10}>
              <Form.Item
                name="customerPaymentDueDate"
                label={t('orders:modal.customerPaymentDueDate')}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  onChange={() => setPaymentDueDateTouched(true)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={14}>
              <Form.Item
                name="customerPaymentNotes"
                label={t('orders:modal.customerPaymentNotes')}
              >
                <Input.TextArea rows={2} placeholder={t('orders:modal.customerPaymentNotesPlaceholder')} />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}
    </div>
  );

  const renderStep3 = () => {
    const allValues = form.getFieldsValue(true);
    const filledProducts = products.filter((product) => String(product?.name || '').trim());

    return (
      <div className="order-create-section">
        {renderSectionHeader(
          t('orders:wizard.sectionTitles.summary'),
          t('orders:wizard.sectionSubtitles.summary')
        )}

        <div className="order-create-review-grid">
          <div className="order-create-review-block">
            <Text type="secondary">{t('orders:wizard.review.orderData')}</Text>
            <Descriptions column={1} size="small">
              <Descriptions.Item label={t('orders:wizard.summary.customer')}>
                {selectedCustomer?.name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('orders:wizard.summary.orderDate')}>
                {allValues.orderDate ? dayjs(allValues.orderDate).format('YYYY-MM-DD') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('orders:modal.clientOrderNumber')}>
                {allValues.clientOrderNumber || '-'}
              </Descriptions.Item>
            </Descriptions>
          </div>

          <div className="order-create-review-block">
            <Text type="secondary">{t('orders:wizard.review.logistics')}</Text>
            <Descriptions column={1} size="small">
              <Descriptions.Item label={t('orders:modal.factoryLocation')}>
                {allValues.factoryLocation || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('orders:modal.deliveryLocation')}>
                {allValues.deliveryLocation || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('orders:expectedDelivery')}>
                {allValues.expectedDelivery ? dayjs(allValues.expectedDelivery).format('YYYY-MM-DD') : '-'}
              </Descriptions.Item>
            </Descriptions>
          </div>
        </div>

        <div className="order-create-review-block order-create-review-products">
          <div className="order-create-review-title-row">
            <Text type="secondary">{t('orders:wizard.review.products')}</Text>
            <Tag bordered={false}>{t('orders:wizard.summary.productsCount')}: {filledProducts.length}</Tag>
          </div>
          <Table
            size="small"
            rowKey={(_, index) => `${index}`}
            dataSource={filledProducts}
            pagination={false}
            columns={[
              {
                title: t('orders:productName'),
                dataIndex: 'name',
              },
              {
                title: t('orders:quantity'),
                dataIndex: 'quantity',
                width: 110,
                render: (quantity: ProductLine['quantity']) => renderQuantity(quantity),
              },
              {
                title: t('orders:orderDetail.saleUnit'),
                width: 120,
                render: (_: unknown, record: ProductLine) => formatUnit(getOrderSaleUnit(record)),
              },
              {
                title: t('orders:orderDetail.dimensions'),
                width: 150,
                render: (_: unknown, record: ProductLine) => formatProductDimensions(record) || '-',
              },
              ...(canEditCustomerSalePrices ? [
                {
                  title: t('orders:unitPrice'),
                  width: 150,
                  render: (_: unknown, record: ProductLine) =>
                    formatCurrency(Number(record.unitPrice || 0), getLineCurrency(record, fallbackCurrency)),
                },
                {
                  title: t('orders:totalPrice'),
                  width: 160,
                  render: (_: unknown, record: ProductLine) =>
                    formatCurrency(getLineTotal(record), getLineCurrency(record, fallbackCurrency)),
                },
              ] : []),
            ]}
          />
        </div>

        <Row gutter={[20, 0]}>
          <Col xs={24} lg={10}>
            <Form.Item
              name="isQuote"
              label={t('orders:modal.createAsQuote')}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24} lg={14}>
            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) => prev.isQuote !== curr.isQuote}
            >
              {({ getFieldValue }) =>
                getFieldValue('isQuote') ? (
                  <Form.Item name="quoteValidUntil" label={t('orders:modal.quoteValidUntil')}>
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                ) : null
              }
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="notes" label={t('orders:notes')}>
          <Input.TextArea rows={2} placeholder={t('orders:wizard.review.notesPlaceholder')} />
        </Form.Item>

        <Divider orientation="left">
          <BellOutlined /> {t('orders:modal.notifications')}
        </Divider>

        <Form.Item
          name="notifyUsers"
          label={t('orders:modal.notifyUsers')}
          tooltip={t('orders:modal.notifyUsersTooltip')}
        >
          <Select
            mode="multiple"
            placeholder={t('orders:modal.selectUsersToNotify')}
            allowClear
            optionFilterProp="children"
          >
            {notificationRecipientOptions.map((user) => (
              <Select.Option key={user.id} value={user.id}>
                {user.name} ({user.email})
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </div>
    );
  };

  return (
    <div className="order-create-shell">
      <header className="order-create-page-header">
        <div>
          <Text type="secondary">{t('orders:newOrder')}</Text>
          <Title level={2}>{t('orders:wizard.customerTitle')}</Title>
        </div>
        <Tag bordered={false} color="blue">
          {t('orders:wizard.summaryPanel.lineCurrencyTag')}
        </Tag>
      </header>

      <Form
        form={form}
        layout="vertical"
        scrollToFirstError
      >
        <Form.Item name="currency" hidden>
          <Input />
        </Form.Item>

        <PresenceBanner scopeType="CUSTOMER_CREATE" others={presenceOthers} style={{ marginBottom: 16 }} />

        <div className="order-create-draft-toolbar-row" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <OrderDraftToolbar
            ref={draftToolbarRef}
            type="CUSTOMER"
            buildPayload={buildDraftPayload}
            applyPayload={applyDraftPayload}
            defaultDraftName={() => selectedCustomer?.name || ''}
            onSaved={() => setLastDraftSavedAt(new Date())}
          />
        </div>

        <div className="order-create-top-stack">
          {renderStepRail()}
          {renderDraftSummary()}
        </div>

        <div className="order-create-layout">
          <main className="order-create-main">
            <div style={{ display: currentStep === 0 ? 'block' : 'none' }}>{renderStep0()}</div>
            <div style={{ display: currentStep === 1 ? 'block' : 'none' }}>{renderStep1()}</div>
            <div style={{ display: currentStep === 2 ? 'block' : 'none' }}>{renderStep2()}</div>
            <div style={{ display: currentStep === 3 ? 'block' : 'none' }}>{renderStep3()}</div>

            <div className="order-create-actions">
              <Button onClick={handleCancel}>{t('orders:wizard.cancel')}</Button>
              <Space>
                <Button disabled={currentStep === 0} onClick={handleBack}>
                  {t('orders:wizard.back')}
                </Button>
                {currentStep < stepItems.length - 1 ? (
                  <Button type="primary" onClick={handleNext}>
                    {t('orders:wizard.next')}
                  </Button>
                ) : (
                  <Button type="primary" loading={submitting} onClick={handleSubmit}>
                    {t('orders:wizard.submitCustomer')}
                  </Button>
                )}
              </Space>
            </div>
          </main>
        </div>
      </Form>
    </div>
  );
};

export default OrderCreateWizard;
